import React from 'react';
import {
  DEFAULT_REPORT_FORMAT, isReportFormat, formatRefRange, getObsValue, getObsName, flagKind, isCriticalFlag,
  FLAG_LABEL, FLAG_MARK, rangeBar, groupTestsByDepartment, collectAbnormal, fmtDateTime, centreAddressLine, centreContactLine,
  centreInitials,
} from './reportHelpers';
import './PathologyReportDocument.css';

const Accreditations = ({ centre }) => (
  <div className="rpt-accred">
    {centre?.isNabl && <span>NABL</span>}
    {centre?.isCap && <span>CAP</span>}
    {centre?.isNabh && <span>NABH</span>}
  </div>
);

const PatientBlock = ({ patient, centre, compact }) => {
  const rows = [
    ['Patient Name', patient.patientName, true],
    ['UHID', patient.patientId || '—'],
    ['Age / Gender', patient.ageGender || '—'],
    ['Lab No.', patient.ledgerTransactionNo, true],
    ['Referred By', patient.doctorName || patient.referringDoctor || 'Self'],
    ['Barcode', patient.barcodeNo || '—'],
    ['Sample', patient.sampleTypeName || '—'],
    ['Centre', patient.centreName || centre?.centreName || '—'],
    ['Received', fmtDateTime(patient.sampleReceiveDate)],
    ['Reported', fmtDateTime(patient.approvedDate)],
  ];
  return (
    <div className={`rpt-patient ${compact ? 'is-compact' : ''}`}>
      {rows.map(([label, value, strong]) => (
        <div className="rpt-patient-item" key={label}>
          <span className="rpt-patient-label">{label}</span>
          <span className={`rpt-patient-val ${strong ? 'is-strong' : ''}`}>{value}</span>
        </div>
      ))}
    </div>
  );
};

const OutsourceBox = ({ test }) => (
  <div className="rpt-outsource">
    <strong>External laboratory result</strong>
    <p>{test.externalResultSummary || 'Result is pending from the reference laboratory.'}</p>
  </div>
);

const ResultsTable = ({ observations, withMarks }) => (
  <table className="rpt-table">
    <thead>
      <tr><th>Test</th><th className="r">Result</th><th>Unit</th><th>Reference Range</th><th className="c">Flag</th></tr>
    </thead>
    <tbody>
      {observations.map((obs, i) => {
        const kind = flagKind(obs.currentFlag || 'N');
        return (
          <tr key={obs.labObservationId || i} className={`rpt-row rpt-row--${kind}`}>
            <td className={obs.isBold ? 'is-bold' : ''}>{getObsName(obs)}</td>
            <td className="r rpt-val">{getObsValue(obs)}</td>
            <td>{obs.unit || ''}</td>
            <td>{formatRefRange(obs)}</td>
            <td className="c">{withMarks && FLAG_MARK[kind] ? <span className="rpt-mark" title={FLAG_LABEL[kind]}>{FLAG_MARK[kind]}</span> : null}</td>
          </tr>
        );
      })}
    </tbody>
  </table>
);

const RangeRow = ({ obs }) => {
  const kind = flagKind(obs.currentFlag || 'N');
  const bar = rangeBar(obs);
  return (
    <div className={`rpt-mrow rpt-mrow--${kind}`}>
      <div className="rpt-mname">{getObsName(obs)}</div>
      <div className="rpt-mval">
        <span className="rpt-pill">{getObsValue(obs)}</span>
        <span className="rpt-munit">{obs.unit || ''}</span>
      </div>
      <div className="rpt-mrange">
        {bar ? (
          <div className="rpt-bar" aria-hidden="true">
            <span className="rpt-bar-band" style={{ left: `${bar.bandStart}%`, width: `${bar.bandEnd - bar.bandStart}%` }} />
            <span className="rpt-bar-dot" style={{ left: `${bar.marker}%` }} />
          </div>
        ) : null}
        <span className="rpt-mref">Ref: {formatRefRange(obs)}</span>
      </div>
      <div className="rpt-mstatus">{FLAG_LABEL[kind]}</div>
    </div>
  );
};

const TestBlock = ({ test, obsMap, layout }) => {
  const observations = obsMap[test.testId] || test.observations || [];
  return (
    <section className="rpt-test">
      <h3 className="rpt-test-title">{test.investigationName}</h3>
      {test.isOutsourced ? <OutsourceBox test={test} />
        : observations.length === 0 ? <p className="rpt-pending">Results are being processed.</p>
        : layout === 'MODERN' ? <div className="rpt-modern-rows">{observations.map((o, i) => <RangeRow key={o.labObservationId || i} obs={o} />)}</div>
        : <ResultsTable observations={observations} withMarks />}
      {test.remarks && <p className="rpt-remarks"><strong>Remarks:</strong> {test.remarks}</p>}
    </section>
  );
};

const Signature = ({ signature, signatureUrl, patient }) => (
  <div className="rpt-signs">
    {signature ? (
      <div className="rpt-sign">
        {signatureUrl && <img src={signatureUrl} alt="Doctor signature" />}
        <strong>{signature.doctorName}</strong>
        <span>{signature.designation || 'Consultant Pathologist'}</span>
        {signature.qualification && <span>{signature.qualification}</span>}
        {signature.medicalRegNo && <span>{signature.regAuthority}: {signature.medicalRegNo}</span>}
        {patient.resultEnteredBy && <small>Processed by: {patient.resultEnteredBy}</small>}
      </div>
    ) : (
      <div className="rpt-sign"><strong>Authorised Signatory</strong><span>Consultant Pathologist</span></div>
    )}
  </div>
);

/**
 * One pathology report, rendered in the layout chosen for the centre. The whole sheet is a single table so the
 * header and footer repeat on every printed page; the LETTERHEAD layout keeps them blank for pre-printed paper.
 */
export const PathologyReportDocument = ({ format, centre, patient, tests = [], testObservationsMap = {}, signature = null, signatureUrl = null }) => {
  const layout = isReportFormat(format) ? format : DEFAULT_REPORT_FORMAT;
  const groups = groupTestsByDepartment(tests);
  const abnormal = layout === 'COMPACT' ? collectAbnormal(tests, testObservationsMap) : [];
  const anyCritical = tests.some((t) => (testObservationsMap[t.testId] || t.observations || []).some((o) => isCriticalFlag(o.currentFlag)));
  const name = centre?.centreName || patient.centreName || 'Laboratory';
  const hasInterim = tests.some((t) => t.approved !== 1);

  const isCoBranded = Boolean(centre?.enableCoBranding || patient?.enableCoBranding);
  const partnerName = centre?.coBrandingPartyName || patient?.partyName || 'Partner Clinic';
  const partnerLogo = centre?.coBrandingLogoUrl || patient?.coBrandingLogoUrl;
  const partnerTagline = centre?.coBrandingTagline || patient?.coBrandingTagline || 'Referred By / Collection Partner';
  const partnerAddress = centre?.coBrandingAddress || patient?.coBrandingAddress;
  const partnerContact = centre?.coBrandingContact || patient?.coBrandingContact;

  const header = layout === 'LETTERHEAD' ? <div className="rpt-letterhead-space" /> : isCoBranded ? (
    <div className="rpt-header rpt-header--cobranded" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '2px solid #0284c7', paddingBottom: '12px', marginBottom: '14px' }}>
      <div className="rpt-cobrand-left" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        {partnerLogo ? (
          <img src={partnerLogo} alt={partnerName} style={{ maxHeight: '55px', maxWidth: '140px', objectFit: 'contain' }} />
        ) : (
          <div className="rpt-emblem" style={{ background: '#0284c7' }}>{centreInitials(partnerName)}</div>
        )}
        <div>
          <h2 style={{ margin: 0, fontSize: '16px', color: '#0f172a' }}>{partnerName}</h2>
          <span style={{ fontSize: '11px', color: '#0284c7', fontWeight: 600 }}>{partnerTagline}</span>
          {partnerAddress && <p style={{ margin: '2px 0 0', fontSize: '10px', color: '#64748b' }}>{partnerAddress}</p>}
          {partnerContact && <p style={{ margin: '1px 0 0', fontSize: '10px', color: '#64748b' }}>{partnerContact}</p>}
        </div>
      </div>

      <div className="rpt-cobrand-middle" style={{ textAlign: 'center', padding: '0 12px', borderLeft: '1px dashed #cbd5e1', borderRight: '1px dashed #cbd5e1' }}>
        <span style={{ fontSize: '9px', fontWeight: 700, color: '#475569', background: '#f1f5f9', padding: '3px 6px', borderRadius: '4px', textTransform: 'uppercase' }}>
          Certified Central Lab
        </span>
      </div>

      <div className="rpt-cobrand-right" style={{ textAlign: 'right' }}>
        <h1 style={{ margin: 0, fontSize: '16px', color: '#1e3a8a' }}>{name}</h1>
        {centreAddressLine(centre) && <p style={{ margin: '2px 0 0', fontSize: '10px', color: '#64748b' }}>{centreAddressLine(centre)}</p>}
        <Accreditations centre={centre} />
      </div>
    </div>
  ) : (
    <div className="rpt-header">
      <div className="rpt-emblem">{centreInitials(name)}</div>
      <div className="rpt-header-text">
        <h1>{name}</h1>
        {centreAddressLine(centre) && <p>{centreAddressLine(centre)}</p>}
        {centreContactLine(centre) && <p>{centreContactLine(centre)}</p>}
      </div>
      <Accreditations centre={centre} />
    </div>
  );

  const footer = layout === 'LETTERHEAD' ? <div className="rpt-letterhead-foot" /> : (
    <div className="rpt-footer">
      <span>{name}{centreContactLine(centre) ? `  •  ${centreContactLine(centre)}` : ''}</span>
      <span>Results relate only to the sample tested and should be correlated clinically.</span>
    </div>
  );

  return (
    <div className={`rpt-sheet rpt-${layout.toLowerCase()}`} data-report-format={layout}>
      <table className="rpt-page">
        <thead><tr><td>{header}</td></tr></thead>
        <tfoot><tr><td>{footer}</td></tr></tfoot>
        <tbody>
          <tr>
            <td>
              <PatientBlock patient={patient} centre={centre} compact={layout === 'COMPACT'} />

              {hasInterim && <div className="rpt-interim">INTERIM REPORT — contains results that are not yet approved</div>}
              {anyCritical && <div className="rpt-critical-note">⚠ Critical values are present in this report.</div>}

              {abnormal.length > 0 && (
                <div className="rpt-summary">
                  <strong>Abnormal results ({abnormal.length})</strong>
                  <ul>
                    {abnormal.map((a, i) => (
                      <li key={i} className={`k-${a.kind}`}>{a.name}: <b>{a.value}</b> {a.unit} <em>({FLAG_LABEL[a.kind]}, ref {a.range})</em></li>
                    ))}
                  </ul>
                </div>
              )}

              {groups.map((g) => (
                <div className="rpt-dept" key={g.name}>
                  <h2 className="rpt-dept-title">{g.name}</h2>
                  {g.tests.map((t) => <TestBlock key={t.testId} test={t} obsMap={testObservationsMap} layout={layout} />)}
                </div>
              ))}

              <p className="rpt-end">— End of report —</p>
              <Signature signature={signature} signatureUrl={signatureUrl} patient={patient} />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default PathologyReportDocument;
